import { createEffect, createSignal, For, Match, onCleanup, Show, Switch, useContext } from 'solid-js';
import decodeHtmlEntities from '../../../tools/decode-html-entities';
import { Section } from '../../../grid-system/section/section';
import { TopCategory, eventsStore } from '../../../stores/events-store';
import { Button } from '../../../ui-components/button/button';
import { Heading } from '../../../ui-components/heading/heading';
import { Checkbox } from '../../../ui-components/inputs/checkbox/checkbox';
import { FlexRow } from '../../../ui-components/utility-style-components/flex';
import { Tag } from '../../event/event-types';
import { CrossIcon } from '../../icons-library/cross';
import { Labels } from '../events-types';
import { Text } from '../../../ui-components/text/text';
import { AppContext } from '../../../app-context-provider/app-context-provider';

type MobileFilterModalProps = {
    closeModal: () => void;
    labels?: Labels;
    eventsCategoriesHeading: string;
    eventsTagsHeading: string;
    eventsLanguagesHeading: string;

    handleOnClickCategory: (category: TopCategory) => void;
    handleOnClickTag: (tag: Tag) => void;
    handleOnClickLanguage: (language: string) => void;
};

export const MobileFilterModal = (props: MobileFilterModalProps) => {
    const { localize } = useContext(AppContext);

    const [ inputRef, setInputRef ] = createSignal<HTMLInputElement | undefined>(undefined);

    createEffect(() => {
        if (!inputRef()) return;

        inputRef()!.tabIndex = -1;
        inputRef()!.focus();
        inputRef()!.tabIndex = 0;
    });

    const onKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            props.closeModal();
        }
    };

    document.addEventListener('keydown', onKeyPress);

    onCleanup(() => {
        document.removeEventListener('keydown', onKeyPress);
    });

    return (
        <div
            class="mobile-filters-modal-container bg-white h-screen fixed left-0 right-0 z-[3] px-6"
            style={{ top: '6.43rem' }}
            onScroll={(e: Event) => e.preventDefault()}
        >
            <Section templateShorthand={[12]} widthType={'bgFull'} backgroundValue={'white'} backgroundType={'color'} heightType={'fill'}>
                <div>
                    <FlexRow justifyContent="end">
                        <CrossIcon size="small" onClick={props.closeModal} ariaLabel={localize('close', 'Close')} />
                    </FlexRow>

                    <div class="pb-2 border-b border-gray mb-6">
                        <Heading noBlockSpacing={true} tag="h3" variant="small">
                            {props.eventsLanguagesHeading}
                        </Heading>
                    </div>
                    <Show when={eventsStore.availableLanguages.length > 0}>
                        <ul class="mb-8">
                            <For each={eventsStore.availableLanguages}>
                                {(language: string, index) => (
                                    <li>
                                        <Checkbox
                                            value={language}
                                            inputRef={(el: HTMLInputElement) => index() === 0 && setInputRef(el)}
                                            whenClicked={() => props.handleOnClickLanguage(language)}
                                            name={language}
                                            isChecked={!!eventsStore?.selectedLanguages?.find((existing) => existing === language)}
                                        />
                                    </li>
                                )}
                            </For>
                        </ul>
                    </Show>

                    <div class="pb-2 border-b border-gray mb-6">
                        <Heading noBlockSpacing={true} tag="h3" variant="small">
                            {props.eventsCategoriesHeading}
                        </Heading>
                    </div>
                    <Switch>
                        <Match when={eventsStore.availableCategories.length === 0}>
                            <Text fontSize="small" displayRedVerticalLine={false}>
                                {props?.labels?.noCategoriesFound || 'No categories found'}
                            </Text>
                        </Match>
                        <Match when={eventsStore.selectedLanguages.length === 0 && eventsStore.availableCategories.length}>
                            <Text fontSize="small" displayRedVerticalLine={false}>
                                {props?.labels?.noLanguageSelected || 'No language selected'}
                            </Text>
                        </Match>
                        <Match when={eventsStore.selectedLanguages.length > 0 && eventsStore.availableCategories.length > 0}>
                            <ul class="mb-8">
                                <For each={eventsStore.availableCategories}>
                                    {(category) => (
                                        <li>
                                            <Checkbox
                                                value={category?.slug}
                                                whenClicked={() => props.handleOnClickCategory(category)}
                                                name={decodeHtmlEntities(category?.name)}
                                                isChecked={!!eventsStore?.selectedCategories?.find((existing) => existing?.slug === category?.slug)}
                                            />
                                        </li>
                                    )}
                                </For>
                            </ul>
                        </Match>
                    </Switch>

                    <div class="pb-2 border-b border-gray mb-6">
                        <Heading noBlockSpacing={true} tag="h3" variant="small">
                            {props.eventsTagsHeading}
                        </Heading>
                    </div>
                    <Switch>
                        <Match when={eventsStore.availableTags.length === 0}>
                            <Text fontSize="small" displayRedVerticalLine={false}>
                                {props?.labels?.noTagsFound || 'No tags found'}
                            </Text>
                        </Match>
                        <Match when={eventsStore.selectedLanguages.length === 0 && eventsStore.availableTags.length}>
                            <Text fontSize="small" displayRedVerticalLine={false}>
                                {props?.labels?.noLanguageSelected || 'No language selected'}
                            </Text>
                        </Match>
                        <Match when={eventsStore.selectedLanguages.length > 0 && eventsStore.availableTags.length > 0}>
                            <ul class="mb-8">
                                <For each={eventsStore.availableTags}>
                                    {(tag) => (
                                        <li>
                                            <Checkbox
                                                value={tag?.slug}
                                                whenClicked={() => props.handleOnClickTag(tag)}
                                                name={decodeHtmlEntities(tag?.name)}
                                                isChecked={!!eventsStore?.selectedTags?.find((existing) => existing?.slug === tag?.slug)}
                                            />
                                        </li>
                                    )}
                                </For>
                            </ul>
                        </Match>
                    </Switch>

                    <FlexRow justifyContent="center">
                        <Button label={props.labels?.showEventsButtonText || 'Show events'} onClick={props.closeModal} />
                    </FlexRow>
                </div>
            </Section>
        </div>
    );
};
